import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createTermsDraft,
  enableAccountTermsRequirement,
  getAccountTerms,
  getTermsPolicy,
  getTermsVersion,
  listTermsMemberStatuses,
  listTermsVersions,
  publishTermsVersion,
  updateTermsDraft,
  updateTermsEnforcement,
} from 'api/terms'
import { getQueryOrganizationId } from 'configs/queryClient'
import { queryKeys } from 'constants/queryKeys'
import { useAuth } from 'contexts/AuthContext'
import type {
  TPublishTermsVersionRequest,
  TTermsDraftRequest,
  TUpdateTermsDraftRequest,
  TUpdateTermsEnforcementRequest,
} from 'types/terms'

/** ADMIN の利用規約管理（公開状態・版）。どの画面の変更後も一覧・詳細・アカウントの状態を取り直す */
function useInvalidateTerms() {
  const queryClient = useQueryClient()
  return () =>
    Promise.all(
      [
        queryKeys.TERMS_POLICY,
        queryKeys.TERMS_VERSIONS,
        queryKeys.TERMS_VERSION_DETAIL,
        queryKeys.TERMS_MEMBER_STATUSES,
        queryKeys.ACCOUNT_TERMS,
      ].map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
    )
}

export function useTermsPolicy() {
  const { user } = useAuth()
  return useQuery({
    queryKey: [queryKeys.TERMS_POLICY, user?.uid],
    enabled: Boolean(user?.uid),
    queryFn: getTermsPolicy,
    retry: false,
  })
}

export function useTermsVersions() {
  const { user } = useAuth()
  return useQuery({
    queryKey: [queryKeys.TERMS_VERSIONS, user?.uid],
    enabled: Boolean(user?.uid),
    queryFn: listTermsVersions,
    retry: false,
  })
}

export function useTermsVersion(versionId: number | undefined) {
  const { user } = useAuth()
  return useQuery({
    queryKey: [queryKeys.TERMS_VERSION_DETAIL, versionId, user?.uid],
    enabled: Boolean(user?.uid) && versionId !== undefined,
    queryFn: () => getTermsVersion(versionId as number),
    retry: false,
  })
}

export function useCreateTermsDraft() {
  const invalidate = useInvalidateTerms()
  return useMutation({
    mutationFn: (request: TTermsDraftRequest) => createTermsDraft(request),
    onSuccess: invalidate,
  })
}

export function useUpdateTermsDraft() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const invalidate = useInvalidateTerms()
  return useMutation({
    mutationFn: ({
      versionId,
      request,
    }: {
      versionId: number
      request: TUpdateTermsDraftRequest
    }) => updateTermsDraft(versionId, request),
    // ADMIN 画面を離れて組織スコープへ入った後に解決しても、その組織のキャッシュへ書かない
    onMutate: () => ({ queryOrganizationId: getQueryOrganizationId() }),
    // 保存した版を版の詳細のキャッシュへ反映する（取り直しが失敗しても保存前の版に戻らない）
    onSuccess: (updated, { versionId }, context) => {
      if (context?.queryOrganizationId !== getQueryOrganizationId()) return
      queryClient.setQueryData(
        [queryKeys.TERMS_VERSION_DETAIL, versionId, user?.uid],
        updated,
      )
    },
    // 409（他の操作で更新・公開済み）でも最新の版を取り直す（古い editRevision で再保存し続けない）
    onSettled: invalidate,
  })
}

export function usePublishTermsVersion() {
  const invalidate = useInvalidateTerms()
  return useMutation({
    mutationFn: ({
      versionId,
      request,
    }: {
      versionId: number
      request: TPublishTermsVersionRequest
    }) => publishTermsVersion(versionId, request),
    // 409（他の操作で更新済み）でも最新の状態へ取り直す
    onSettled: invalidate,
  })
}

export function useUpdateTermsEnforcement() {
  const invalidate = useInvalidateTerms()
  return useMutation({
    mutationFn: (request: TUpdateTermsEnforcementRequest) =>
      updateTermsEnforcement(request),
    onSettled: invalidate,
  })
}

/** アカウント一覧に表示する全アカウントの同意状態 */
export function useTermsMemberStatuses() {
  const { user } = useAuth()
  return useQuery({
    queryKey: [queryKeys.TERMS_MEMBER_STATUSES, user?.uid],
    enabled: Boolean(user?.uid),
    queryFn: listTermsMemberStatuses,
    retry: false,
  })
}

export function useAccountTerms(accountId: number | undefined) {
  const { user } = useAuth()
  return useQuery({
    queryKey: [queryKeys.ACCOUNT_TERMS, accountId, user?.uid],
    enabled: Boolean(user?.uid) && accountId !== undefined,
    queryFn: () => getAccountTerms(accountId as number),
    retry: false,
  })
}

export function useEnableAccountTermsRequirement() {
  const invalidate = useInvalidateTerms()
  return useMutation({
    mutationFn: (accountId: number) => enableAccountTermsRequirement(accountId),
    onSettled: invalidate,
  })
}
